import { create } from "zustand";
import { getStoredToken, setStoredToken } from "../api/client";
import {
  getProfile,
  login as apiLogin,
  logout as apiLogout,
  normalizeProfessionalUser,
  refreshToken,
  verifyOtp,
} from "../api/authProfessional";
import type { ProfessionalUser } from "../api/authProfessional";

let otpLoginInProgress = false;

export type Professional = {
  id?: string;
  nip?: string;
  email?: string;
  nom?: string;
  prenom?: string;
  specialite?: string;
  type?: string;
  statut?: string;
};

export type SignInCredentials = { email: string; password: string };

function toProfessional(user: ProfessionalUser | null): Professional | null {
  if (!user) return null;
  const merged = normalizeProfessionalUser(user) ?? user;
  const email = typeof merged.email === "string" ? merged.email.trim() : "";
  const nom = typeof merged.nom === "string" ? merged.nom.trim() : "";
  const prenom = typeof merged.prenom === "string" ? merged.prenom.trim() : "";
  const specialite = typeof merged.specialite === "string" ? merged.specialite.trim() : "";
  const type = typeof merged.type === "string" ? merged.type.trim() : "";
  const statut = typeof merged.statut === "string" ? merged.statut.trim() : "";
  const id = typeof merged.id === "string" ? merged.id.trim() : "";
  const nip = typeof merged.nip === "string" ? merged.nip.trim() : "";

  if (!id && !email && !nom && !prenom && !nip) return null;
  return {
    id: id || undefined,
    nip: nip || undefined,
    email: email || undefined,
    nom: nom || undefined,
    prenom: prenom || undefined,
    specialite: specialite || undefined,
    type: type || undefined,
    statut: statut || undefined,
  };
}

/** Mappe la réponse profil API vers l’objet `Professional` du store. */
export function professionalFromUser(user: ProfessionalUser | null): Professional | null {
  return toProfessional(user);
}

export type SignInResult =
  | { ok: true; requiresOtp?: boolean }
  | { ok: false; message: string };

type AuthState = {
  professional: Professional | null;
  isLoading: boolean;
  setProfessional: (pro: Professional | null) => void;
  signIn: (credentials: SignInCredentials) => Promise<SignInResult>;
  confirmLoginOtp: (email: string, code: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  clearAuth: () => void;
  initAuth: () => Promise<void>;
};

export const useAuthProfessionalStore = create<AuthState>()((set) => ({
  professional: null,
  isLoading: true,

  setProfessional: (pro) => set({ professional: pro }),

  signIn: async (credentials) => {
    const result = await apiLogin(credentials.email, credentials.password);
    if (!result.ok) return { ok: false, message: result.message };
    return { ok: true, requiresOtp: true };
  },

  confirmLoginOtp: async (email, code) => {
    otpLoginInProgress = true;
    set({ isLoading: true });
    try {
      const result = await verifyOtp(email, code);
      if (!result.ok) return { ok: false, message: result.message };
      if (result.data?.user) set({ professional: toProfessional(result.data.user) });
      return { ok: true };
    } finally {
      otpLoginInProgress = false;
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    await apiLogout();
    set({ professional: null });
  },

  clearAuth: () => {
    setStoredToken(null);
    set({ professional: null, isLoading: false });
  },

  initAuth: async () => {
    set({ isLoading: true });
    try {
      const tokenAtStart = getStoredToken();
      const refresh = await refreshToken();
      if (!refresh.ok) {
        if (!getStoredToken() && !tokenAtStart) {
          setStoredToken(null);
          set({ professional: null });
        }
        return;
      }

      const profileRes = await getProfile({ silent401: true });
      if (profileRes.ok) {
        set({ professional: toProfessional(profileRes.data) });
      } else if (!getStoredToken() && !tokenAtStart) {
        set({ professional: null });
      }
    } finally {
      if (!otpLoginInProgress) set({ isLoading: false });
    }
  },
}));

export function useProfessionalAuth() {
  const professional = useAuthProfessionalStore((s) => s.professional);
  const isLoading = useAuthProfessionalStore((s) => s.isLoading);
  const setProfessional = useAuthProfessionalStore((s) => s.setProfessional);
  const signIn = useAuthProfessionalStore((s) => s.signIn);
  const confirmLoginOtp = useAuthProfessionalStore((s) => s.confirmLoginOtp);
  const signOut = useAuthProfessionalStore((s) => s.signOut);

  return {
    professional,
    setProfessional,
    isAuthenticated: !isLoading && !!professional,
    isLoading,
    signIn,
    confirmLoginOtp,
    signOut,
  };
}
